import {
  faPlus,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import Pagination from 'components/pagination';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Button, FormControl, InputGroup, Modal } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import TaskTemplateService from 'services/TaskTemplate.service';
import TicketTasksService from 'services/TicketTasks.service';
import useNotification from "hooks/useNotification";
import DialogModal from './DialogModal';

const TaskTemplateAssociateModal = ({
  show,
  onHide,
  ticketId,
  onTemplateAssociated,
  refreshTasks
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const formatDateTime = useFormatDateTime();
  const [isLoading, setIsLoading] = useState(false);
  const [isAssociating, setIsAssociating] = useState(false);
  const [taskTemplates, setTaskTemplates] = useState({
    content: [],
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    includeInactives: false,
    searchTerm: ''
  });

  const [filterNameCustomer, setFilterNameCustomer] = useState('');

  const [showAssociateConfirmModal, setShowAssociateConfirmModal] = useState(false);
  const [templateToAssociate, setTemplateToAssociate] = useState(null);

  const isEmpty = !isLoading && taskTemplates.content.length === 0;

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await TaskTemplateService.list(queryParams);
      if (response.content) {
        setTaskTemplates(response);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      showErrorNotification('Erro ao carregar templates de tarefas');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (show) {
      getData();
    }
  }, [filters, show]);

  // Funções de busca
  const handleSearchChange = e => {
    const searchValue = e.target.value;
    setFilterNameCustomer(searchValue);

    if (searchValue === '') {
      setFilters(prevFilters => ({
        ...prevFilters,
        page: 0,
        searchTerm: ''
      }));
    }
  };

  const handleSearch = () => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      searchTerm: filterNameCustomer
    }));
  };

  const confirmAssociate = (template) => {
    setTemplateToAssociate(template);
    setShowAssociateConfirmModal(true);
  };

  const handleAssociateTemplate = async () => {
    if (!templateToAssociate) return;

    setIsAssociating(true);
    try {
      await TicketTasksService.associateTemplate(ticketId, templateToAssociate.id);
      showSuccessNotification('Template associado com sucesso');

      // Refresh tasks in parent component
      if (refreshTasks) {
        refreshTasks();
      }

      // Call parent callback
      if (onTemplateAssociated) {
        onTemplateAssociated();
      }
      setShowAssociateConfirmModal(false);
      setTemplateToAssociate(null);

      // Close modal after short delay
      setTimeout(() => {
        onHide();
      }, 1500);

    } catch (error) {
      console.error('Erro ao associar template:', error);
      showErrorNotification('Erro ao associar template ao ticket');
      setShowAssociateConfirmModal(false);
      setTemplateToAssociate(null);
    }
    setIsAssociating(false);
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { title } = rowData.row.original;
        return <span className="fw-medium">{title}</span>;
      }
    },
    {
      accessorKey: 'serviceCatalog',
      header: 'Catálogo de serviço',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { serviceCatalog, subcategory, category } = rowData.row.original;
        return serviceCatalog ? (
          <span>{`${serviceCatalog.name} > ${category.name} > ${subcategory.name}`}</span>
        ) : '-';
      }
    },
    {
      accessorKey: 'applyToAllCustomers',
      header: 'Clientes',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { applyToAllCustomers, customers } = rowData.row.original;
        const customersStr = customers.map(customer => customer.name).join(', ');

        if (applyToAllCustomers) {
          return <span className="">Todos os clientes</span>;
        } else if (customers && customers.length > 0) {

          return <div
            title={customersStr}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '250px'
            }}
          >
            {customersStr}
          </div>;
        } else {

          return <span>Associação manual</span>;
        }
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Criado em',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        return creationDate ? (
          <span className="text-muted">{formatDateTime(creationDate)}</span>
        ) : '-';
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900 text-center' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <div className="text-center">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => confirmAssociate(item)}
              disabled={isAssociating}
              title="Associar template"
            >
              {isAssociating ? (
                <FontAwesomeIcon icon={faSpinner} spin size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} size="sm" className="me-1" />
                  Associar
                </>
              )}
            </Button>
          </div>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: taskTemplates.content,
    columns
  });

  const handleClose = () => {
    onHide();
  };

  return (
    <>
      <DialogModal
        showModal={showAssociateConfirmModal}
        handleClose={() => setShowAssociateConfirmModal(false)}
        title="Confirmar associação"
        message={`Tem certeza que deseja associar o template ao ticket?`}
        response={handleAssociateTemplate}
      />

      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Template de Tarefas</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0">
              Selecione um template para adicionar as tarefas ao ticket
            </p>
          </div>

          <Flex justifyContent="between" className="mb-4">
            <InputGroup className="position-relative input-search-width">
              <FormControl
                size="sm"
                id="search"
                type="search"
                className="shadow-none"
                placeholder="Buscar por nome"
                value={filterNameCustomer}
                onChange={handleSearchChange}
                style={{ borderRadius: '1px 0px 0px 1px' }}
              />
              <Button
                size="sm"
                variant="outline-secondary"
                className="border-300 hover-border-secondary"
                onClick={handleSearch}
              >
                <FontAwesomeIcon icon="search" className="fs-10" />
              </Button>
            </InputGroup>

          </Flex>

          {isLoading ? (
            <Skeleton count={5} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <div style={{ minHeight: '300px' }}>
              <EmptyList
                imgSize={200}
                message="Nenhum template de tarefa encontrado"
              />
            </div>
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden',
                    hover: true
                  }}
                />
              </AdvanceTableProvider>

              <div className="mt-3">
                <Pagination
                  data={taskTemplates}
                  filters={filters}
                  setFilters={setFilters}
                />
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="falcon-default" onClick={handleClose}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskTemplateAssociateModal;
